import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { drawRandomCard, tarotDeck } from '../../data/tarot';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const TarotTable = ({ onClose, onConfirm }) => {
  const [phase, setPhase] = useState('mode');
  const [spreadMode, setSpreadMode] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [spreadType, setSpreadType] = useState('past-present-future');
  const [cards, setCards] = useState([]);
  const [selectedIndices, setSelectedIndices] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isShuffling, setIsShuffling] = useState(false);
  const [customLabels, setCustomLabels] = useState(['', '', '']);
  const [customQuestion, setCustomQuestion] = useState('');

  const fanCards = [...Array(40)];

  const categories = [
    { id: 'daily', name: '今日運勢', subtitle: 'Daily Fortune', icon: '☀️', desc: '今天的整體能量與建議' },
    { id: 'love', name: '感情關係', subtitle: 'Love & Relationship', icon: '💕', desc: '戀愛、曖昧、伴侶關係' },
    { id: 'career', name: '工作事業', subtitle: 'Career & Ambition', icon: '💼', desc: '職場發展、事業方向' },
    { id: 'wealth', name: '財運金錢', subtitle: 'Wealth & Prosperity', icon: '💰', desc: '財務狀況、投資理財' },
    { id: 'social', name: '人際關係', subtitle: 'Social Connection', icon: '👥', desc: '友情、家庭、社交' },
    { id: 'custom', name: '自由提問', subtitle: 'Open Inquiry', icon: '✨', desc: '自訂問題或綜合指引' }
  ];

  const baseSpreadTypes = {
    'past-present-future': {
      name: '時間之流',
      subtitle: 'Past · Present · Future',
      positions: [
        { title: '過去', subtitle: 'The Past', desc: '影響你的根源' },
        { title: '現在', subtitle: 'The Present', desc: '當下的核心狀態' },
        { title: '未來', subtitle: 'The Future', desc: '可能的發展方向' }
      ]
    },
    'problem-cause-solution': {
      name: '問題解析',
      subtitle: 'Problem · Cause · Solution',
      positions: [
        { title: '問題', subtitle: 'The Problem', desc: '當前面臨的挑戰' },
        { title: '原因', subtitle: 'The Cause', desc: '問題的根源' },
        { title: '建議', subtitle: 'The Solution', desc: '前進的方向' }
      ]
    },
    'you-them-relationship': {
      name: '關係探索',
      subtitle: 'You · Them · Connection',
      positions: [
        { title: '你', subtitle: 'You', desc: '你的內在狀態' },
        { title: '對方', subtitle: 'Them', desc: '對方的能量' },
        { title: '關係', subtitle: 'Relationship', desc: '彼此的連結' }
      ]
    },
    'custom': {
      name: '自由牌陣',
      subtitle: 'Custom Spread',
      positions: [
        { title: '第一張', subtitle: 'Card 1', desc: '自定義問題' },
        { title: '第二張', subtitle: 'Card 2', desc: '自定義問題' },
        { title: '第三張', subtitle: 'Card 3', desc: '自定義問題' }
      ]
    }
  };

  const getCurrentSpreadType = () => {
    const base = baseSpreadTypes[spreadType];
    if (spreadType === 'custom') {
      return {
        ...base,
        positions: [
          { title: customLabels[0].trim() || '第一張', subtitle: 'Card 1', desc: '你的直覺指引' },
          { title: customLabels[1].trim() || '第二張', subtitle: 'Card 2', desc: '潛意識訊息' },
          { title: customLabels[2].trim() || '第三張', subtitle: 'Card 3', desc: '宇宙的回應' }
        ]
      };
    }
    return base;
  };

  const currentSpreadType = getCurrentSpreadType();

  const handleSelectMode = (mode) => {
    setSpreadMode(mode);
    if (mode === 'single') {
      setPhase('category');
    } else {
      setPhase('spreadTypeSelection');
    }
  };

  const handleSelectCategory = (categoryId) => {
    setSelectedCategory(categoryId);
    if (categoryId === 'custom') {
      setPhase('customInput');
    } else {
      setPhase('idle');
    }
  };

  const handleConfirmCustomQuestion = () => {
    setPhase('idle');
  };

  const handleSelectSpreadType = (type) => {
    setSpreadType(type);
    if (type === 'custom') {
      setPhase('customInput');
    } else {
      setPhase('idle');
    }
  };

  // 🔧 修復:新增自定義牌陣確認函數
  const handleConfirmCustomInput = () => {
    setPhase('idle');
  };

  const handleShuffle = () => {
    if (isShuffling || isDrawing || phase !== 'idle') return;
    setIsShuffling(true);
    setSelectedIndices([]);
    setCards([]);
    setTimeout(() => {
      setIsShuffling(false);
    }, 1200);
  };

  const handleSelectCard = (index) => {
    if (phase !== 'idle' || isDrawing || isShuffling) return;

    const newSelectedIndices = [...selectedIndices, index];
    setSelectedIndices(newSelectedIndices);
    setIsDrawing(true);

    setTimeout(() => {
      if (spreadMode === 'single') {
        const drawnCard = drawRandomCard();
        drawnCard.questionCategory = selectedCategory;
        drawnCard.customQuestion = customQuestion;
        setCards([drawnCard]);
        setPhase('revealed');
        setIsDrawing(false);
      } else {
        const newCard = drawRandomCard();
        const newCards = [...cards, newCard];
        setCards(newCards);
        if (newCards.length === 3) {
          setPhase('revealed');
        }
        setIsDrawing(false);
      }
    }, 1000);
  };

  const handleConfirmSpread = () => {
    if (spreadMode === 'single') {
      onConfirm(cards[0]);
    } else {
      const spreadDescription = cards.map((card, i) =>
        `【${currentSpreadType.positions[i].title}】${card.name}(${card.statusText}): ${card.currentMeaning.keywords.join('、')}`
      ).join('\n');

      onConfirm({
        ...cards[0],
        name: currentSpreadType.name,
        nameEn: currentSpreadType.subtitle,
        statusText: '',
        currentMeaning: {
          keywords: cards.flatMap(c => c.currentMeaning.keywords).slice(0, 5),
          meaning: `我為你抽出了【${currentSpreadType.name}】牌陣:\n${spreadDescription}`
        },
        isThreeCardSpread: true,
        threeCards: cards,
        spreadType: currentSpreadType
      });
    }
  };

  const getPhaseTitle = () => {
    switch (phase) {
      case 'mode': return '選擇你的牌陣';
      case 'category': return '選擇問題類別';
      case 'spreadTypeSelection': return '選擇牌陣類型';
      case 'customInput': return spreadMode === 'single' ? '寫下你的問題' : '定義你的問題';
      case 'idle':
        if (isShuffling) return '傾聽牌靈...';
        return spreadMode === 'triple' && cards.length < 3 ? `請抽第 ${cards.length + 1} 張牌` : '命運的指引';
      case 'revealed': return '牌面揭曉';
      default: return '傾聽潛意識...';
    }
  };

  const getPhaseSubtitle = () => {
    switch (phase) {
      case 'mode': return 'Choose your spread';
      case 'category': return 'What guidance do you seek?';
      case 'spreadTypeSelection': return 'Select spread type';
      case 'customInput': return spreadMode === 'single' ? 'Speak your heart' : 'Define your inquiry';
      case 'idle':
        if (isShuffling) return 'Cleansing the deck';
        if (spreadMode === 'single' && selectedCategory) {
          const cat = categories.find(c => c.id === selectedCategory);
          return cat ? cat.subtitle : '憑直覺選擇一張牌';
        }
        return spreadMode === 'triple' ? currentSpreadType.positions[cards.length]?.title : '憑直覺選擇一張牌';
      case 'revealed': return 'The Oracle has spoken';
      default: return 'The cards are shifting';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
      className="fixed inset-0 z-[100] flex flex-col bg-gradient-to-br from-[#160a18] via-[#050508] to-[#0a0505] overflow-hidden"
    >
      <div className="grain-overlay opacity-50 pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgba(245,158,11,0.05)_0%,_transparent_60%)] pointer-events-none" />

      <button
        onClick={onClose}
        className="absolute top-6 md:top-10 left-6 md:left-10 text-gray-500 hover:text-amber-400 z-50 p-2 transition-colors duration-300"
      >
        <SafeIcon icon={FiIcons.FiX} className="text-2xl md:text-3xl font-light" />
      </button>

      <div className="flex-1 w-full overflow-y-auto no-scrollbar flex flex-col relative z-30">
        <div className="m-auto w-full flex flex-col items-center py-20 md:py-24 px-4 min-h-full">
          <motion.div
            key={phase + cards.length}
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center text-center mb-10 md:mb-14 flex-none w-full"
          >
            <h3 className="text-amber-500/90 font-serif tracking-[0.4em] text-xl md:text-2xl drop-shadow-[0_0_10px_rgba(245,158,11,0.3)]">
              {getPhaseTitle()}
            </h3>
            <p className="text-amber-400/50 text-[10px] md:text-xs tracking-widest mt-3 uppercase">
              {getPhaseSubtitle()}
            </p>
          </motion.div>

          <div className="w-full flex justify-center flex-none">
            <AnimatePresence mode="wait">
              {/* 選擇模式 */}
              {phase === 'mode' && (
                <motion.div
                  key="mode-selection"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.5 }}
                  className="flex flex-col md:flex-row gap-10 md:gap-16 items-center justify-center w-full"
                >
                  <button
                    onClick={() => handleSelectMode('single')}
                    className="group relative flex flex-col items-center"
                  >
                    <div className="w-40 h-64 md:w-52 md:h-80 rounded-xl border border-amber-500/30 bg-[#0a0a0c] overflow-hidden transition-all duration-500 group-hover:border-amber-400 group-hover:shadow-[0_0_40px_rgba(245,158,11,0.2)] group-hover:-translate-y-2">
                      <img src={tarotDeck.cardBackImg} className="w-full h-full object-cover opacity-80 group-hover:opacity-100" alt="single card" />
                    </div>
                    <span className="mt-8 text-amber-400 font-serif text-lg md:text-xl tracking-widest group-hover:text-amber-300">
                      單張指引
                    </span>
                    <span className="text-gray-500 text-[10px] md:text-xs tracking-wider mt-2">
                      Simple & Direct
                    </span>
                  </button>

                  <button
                    onClick={() => handleSelectMode('triple')}
                    className="group relative flex flex-col items-center mt-12 md:mt-0"
                  >
                    <div className="relative w-56 h-64 md:w-72 md:h-80 flex items-center justify-center">
                      {[0, 1, 2].map((i) => (
                        <div
                          key={i}
                          className="absolute w-36 h-56 md:w-44 md:h-72 rounded-xl border border-amber-500/30 bg-[#0a0a0c] overflow-hidden transition-all duration-500 group-hover:border-amber-400 group-hover:shadow-[0_0_30px_rgba(245,158,11,0.2)]"
                          style={{
                            transform: `translateX(${(i - 1) * 45}px) rotate(${(i - 1) * 10}deg) translateY(${Math.abs(i - 1) * 10}px)`,
                            zIndex: i === 1 ? 3 : i
                          }}
                        >
                          <img src={tarotDeck.cardBackImg} className="w-full h-full object-cover opacity-80 group-hover:opacity-100" alt={`card ${i}`} />
                        </div>
                      ))}
                    </div>
                    <span className="mt-8 text-amber-400 font-serif text-lg md:text-xl tracking-widest group-hover:text-amber-300">
                      三張牌陣
                    </span>
                    <span className="text-gray-500 text-[10px] md:text-xs tracking-wider mt-2">
                      Deep Insight
                    </span>
                  </button>
                </motion.div>
              )}

              {/* 選擇問題類別 */}
              {phase === 'category' && (
                <motion.div
                  key="category-selection"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-4xl w-full"
                >
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleSelectCategory(cat.id)}
                      className="group relative p-6 md:p-8 bg-[#0a0a0c]/60 border border-amber-500/20 rounded-2xl hover:border-amber-400 hover:bg-[#110d0a] transition-all duration-500 text-center overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-3xl group-hover:bg-amber-500/10 transition-colors" />
                      <div className="text-4xl mb-4 opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all relative z-10">
                        {cat.icon}
                      </div>
                      <h4 className="text-amber-400 font-serif text-lg tracking-widest mb-2 relative z-10">
                        {cat.name}
                      </h4>
                      <p className="text-amber-500/50 text-[10px] tracking-wider uppercase relative z-10">
                        {cat.subtitle}
                      </p>
                    </button>
                  ))}
                </motion.div>
              )}

              {/* 選擇牌陣類型 */}
              {phase === 'spreadTypeSelection' && (
                <motion.div
                  key="spread-selection"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-4xl w-full"
                >
                  {Object.entries(baseSpreadTypes).map(([key, type]) => (
                    <button
                      key={key}
                      onClick={() => handleSelectSpreadType(key)}
                      className="group p-6 md:p-8 bg-[#0a0a0c]/60 border border-amber-500/20 rounded-2xl hover:border-amber-400 hover:bg-[#110d0a] transition-all duration-500 text-left relative overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-3xl group-hover:bg-amber-500/10 transition-colors" />
                      <h4 className="text-amber-400 font-serif text-lg md:text-xl tracking-widest mb-2 relative z-10">
                        {type.name}
                      </h4>
                      <p className="text-amber-500/50 text-[10px] md:text-xs tracking-wider mb-6 relative z-10">
                        {type.subtitle}
                      </p>
                      <div className="flex flex-col space-y-3 relative z-10">
                        {type.positions.map((pos, i) => (
                          <div key={i} className="flex items-center space-x-3 text-xs text-gray-400">
                            <span className="w-5 h-5 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 text-[10px] flex-shrink-0">
                              {i + 1}
                            </span>
                            <span className="tracking-widest">{pos.title}</span>
                          </div>
                        ))}
                      </div>
                    </button>
                  ))}
                </motion.div>
              )}

              {/* 自由提問輸入 */}
              {phase === 'customInput' && (
                <motion.div
                  key="custom-input"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="w-full max-w-md flex flex-col items-center"
                >
                  {spreadMode === 'single' ? (
                    <>
                      <div className="w-full mb-8">
                        <label className="text-amber-500/70 text-[10px] tracking-widest mb-3 block text-center">
                          在心中默念你的問題,或寫下來
                        </label>
                        <textarea
                          maxLength={100}
                          rows={4}
                          value={customQuestion}
                          onChange={(e) => setCustomQuestion(e.target.value)}
                          placeholder="例如:我該如何面對這段關係的不確定性?"
                          className="w-full bg-[#0a0a0c]/80 border border-amber-500/30 rounded-xl px-4 py-3 text-amber-50 text-sm focus:outline-none focus:border-amber-400 focus:shadow-[0_0_15px_rgba(245,158,11,0.2)] transition-all placeholder:text-gray-600 placeholder:text-xs resize-none"
                        />
                      </div>
                      <button
                        onClick={handleConfirmCustomQuestion}
                        className="px-8 py-3 bg-amber-500/10 border border-amber-400 text-amber-400 rounded-full text-sm tracking-widest hover:bg-amber-400 hover:text-black transition-all duration-300 shadow-[0_0_20px_rgba(245,158,11,0.1)] hover:shadow-[0_0_30px_rgba(245,158,11,0.4)]"
                      >
                        開始抽牌
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="w-full space-y-5 mb-10">
                        {[
                          { label: '第一張牌代表', placeholder: '例如:我該如何面對這件事?', default: '第一張' },
                          { label: '第二張牌代表', placeholder: '例如:對方的真實想法?', default: '第二張' },
                          { label: '第三張牌代表', placeholder: '例如:未來的可能發展?', default: '第三張' }
                        ].map((item, i) => (
                          <div key={i} className="flex flex-col">
                            <label className="text-amber-500/70 text-[10px] tracking-widest mb-2 pl-2">
                              {item.label}
                            </label>
                            <input
                              type="text"
                              maxLength={15}
                              value={customLabels[i]}
                              onChange={(e) => {
                                const newLabels = [...customLabels];
                                newLabels[i] = e.target.value;
                                setCustomLabels(newLabels);
                              }}
                              placeholder={item.placeholder}
                              className="bg-[#0a0a0c]/80 border border-amber-500/30 rounded-xl px-4 py-3 text-amber-50 text-sm focus:outline-none focus:border-amber-400 focus:shadow-[0_0_15px_rgba(245,158,11,0.2)] transition-all placeholder:text-gray-600"
                            />
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={handleConfirmCustomInput}
                        className="px-8 py-3 bg-amber-500/10 border border-amber-400 text-amber-400 rounded-full text-sm tracking-widest hover:bg-amber-400 hover:text-black transition-all duration-300 shadow-[0_0_20px_rgba(245,158,11,0.1)] hover:shadow-[0_0_30px_rgba(245,158,11,0.4)]"
                      >
                        確認牌陣,開始抽牌
                      </button>
                    </>
                  )}
                </motion.div>
              )}

              {/* 扇形巨大攤牌 */}
              {phase === 'idle' && (
                <motion.div
                  key="drawing-fan"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, filter: 'blur(10px)' }}
                  className="w-full flex flex-col items-center justify-center"
                >
                  <div className="relative w-full h-[35vh] md:h-[45vh] flex items-end justify-center mt-2 md:mt-4">
                    {fanCards.map((_, i) => {
                      const totalCards = fanCards.length;
                      const middleIndex = (totalCards - 1) / 2;
                      const offset = i - middleIndex;
                      const rotation = isShuffling ? 0 : offset * 3.5;
                      const isSelected = selectedIndices.includes(i);

                      return (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, y: 200, rotateZ: 0 }}
                          animate={{
                            opacity: isSelected ? 0 : 1,
                            rotateZ: rotation,
                            y: isShuffling ? 0 : 0,
                            scale: isSelected ? 0.8 : 1
                          }}
                          transition={{
                            duration: isShuffling ? 0.5 : 0.8,
                            delay: isShuffling ? 0 : i * 0.02,
                            ease: 'easeOut'
                          }}
                          onClick={() => !isSelected && handleSelectCard(i)}
                          className="absolute w-24 h-40 md:w-32 md:h-52 cursor-pointer group"
                          style={{
                            transformOrigin: 'center -100%',
                            zIndex: totalCards - Math.abs(offset),
                            pointerEvents: isSelected || isShuffling ? 'none' : 'auto'
                          }}
                        >
                          <div className="w-full h-full rounded-md md:rounded-lg transition-all duration-300 transform group-hover:-translate-y-6 group-hover:scale-110 shadow-[0_5px_15px_rgba(0,0,0,0.8)] group-hover:shadow-[0_0_30px_rgba(245,158,11,0.5)] border border-amber-500/30 group-hover:border-amber-400 bg-[#0a0a0c]">
                            <img
                              src={tarotDeck.cardBackImg}
                              className="w-full h-full object-cover rounded-md md:rounded-lg opacity-90 group-hover:opacity-100 mix-blend-lighten"
                              alt="tarot back"
                            />
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>

                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1 }}
                    onClick={handleShuffle}
                    disabled={isShuffling || isDrawing}
                    className="mt-8 md:mt-12 flex items-center space-x-2 px-6 py-2.5 bg-[#0a0a0c]/80 border border-amber-500/30 rounded-full text-amber-500/80 text-[10px] md:text-xs tracking-[0.2em] backdrop-blur-md hover:bg-amber-500/10 hover:border-amber-400 hover:text-amber-400 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed z-50"
                  >
                    <SafeIcon icon={FiIcons.FiRefreshCcw} className={isShuffling ? 'animate-spin' : ''} />
                    <span>淨化牌陣 · 重新洗牌</span>
                  </motion.button>
                </motion.div>
              )}

              {/* 翻牌與展示 */}
              {(phase === 'drawing' || phase === 'revealed') && cards.length > 0 && (
                <motion.div
                  key="revealed-cards"
                  className="w-full flex flex-col items-center justify-center"
                >
                  <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
                    {spreadMode === 'single' ? (
                      <motion.div
                        initial={{ rotateY: 0, scale: 0.8, opacity: 0 }}
                        animate={{
                          rotateY: phase === 'revealed' ? 180 : 0,
                          scale: phase === 'revealed' ? 1.05 : 1,
                          opacity: 1
                        }}
                        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                        style={{ transformStyle: 'preserve-3d' }}
                        className="w-64 h-[28rem] md:w-80 md:h-[36rem] relative"
                      >
                        <div
                          className="absolute inset-0 rounded-xl shadow-2xl border border-amber-500/40 bg-[#0a0a0c]"
                          style={{ backfaceVisibility: 'hidden' }}
                        >
                          <img src={tarotDeck.cardBackImg} className="w-full h-full object-cover rounded-xl" alt="back" />
                        </div>
                        <div
                          className="absolute inset-0 rounded-xl shadow-[0_0_50px_rgba(245,158,11,0.3)] border border-amber-400 bg-[#0a0a0c]"
                          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                        >
                          <img
                            src={cards[0].img}
                            className={`w-full h-full object-cover rounded-xl ${!cards[0].isUpright ? 'rotate-180' : ''}`}
                            alt={cards[0].name}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent rounded-xl flex flex-col justify-end p-6 md:p-8 pointer-events-none">
                            <span className="text-amber-400 font-serif text-2xl md:text-3xl tracking-widest drop-shadow-md">
                              {cards[0].name}
                              <span className="text-sm font-sans mx-2 opacity-80">
                                {cards[0].statusText}
                              </span>
                            </span>
                            <span className="text-gray-300 text-xs md:text-sm tracking-[0.3em] mt-2 uppercase">
                              {cards[0].nameEn}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ) : (
                      cards.map((card, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ rotateY: 0, y: 50, scale: 0.8, opacity: 0 }}
                          animate={{ rotateY: 180, y: 0, scale: 1, opacity: 1 }}
                          transition={{ duration: 1.2, delay: idx * 0.3, ease: [0.22, 1, 0.36, 1] }}
                          style={{ transformStyle: 'preserve-3d' }}
                          className="w-40 h-[17rem] md:w-56 md:h-[24rem] relative"
                        >
                          <div
                            className="absolute inset-0 rounded-lg shadow-xl border border-amber-500/30 bg-[#0a0a0c]"
                            style={{ backfaceVisibility: 'hidden' }}
                          >
                            <img src={tarotDeck.cardBackImg} className="w-full h-full object-cover rounded-lg" alt="back" />
                          </div>
                          <div
                            className="absolute inset-0 rounded-lg shadow-[0_0_30px_rgba(245,158,11,0.25)] border border-amber-400 bg-[#0a0a0c] flex flex-col overflow-hidden"
                            style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                          >
                            <img
                              src={card.img}
                              className={`w-full flex-1 object-cover min-h-0 ${!card.isUpright ? 'rotate-180' : ''}`}
                              alt={card.name}
                            />
                            <div className="bg-black/95 px-3 py-2 md:px-5 md:py-3 rounded-b-lg border-t border-amber-500/30 flex-none h-[4.5rem] md:h-[5.5rem] flex flex-col justify-center">
                              <span className="block text-amber-500/80 text-[9px] md:text-[10px] tracking-widest uppercase mb-1 truncate">
                                {currentSpreadType.positions[idx].title}
                              </span>
                              <span className="block text-amber-300 font-serif text-sm md:text-base tracking-wide leading-tight">
                                {card.name}
                              </span>
                              <span className="block text-gray-400 text-[9px] md:text-[10px] mt-0.5">
                                {card.statusText}
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      ))
                    )}
                  </div>

                  <AnimatePresence>
                    {phase === 'revealed' && (
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: spreadMode === 'triple' ? 1.5 : 1, duration: 0.8 }}
                        className="w-full max-w-2xl text-center mt-16 md:mt-20 mb-12 md:mb-16 flex-none"
                      >
                        {spreadMode === 'single' && (
                          <>
                            <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-6">
                              {cards[0].currentMeaning.keywords.map((kw, i) => (
                                <span
                                  key={i}
                                  className="px-4 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-300 text-[10px] md:text-xs tracking-widest backdrop-blur-sm"
                                >
                                  {kw}
                                </span>
                              ))}
                            </div>
                            <p className="text-gray-300 text-xs md:text-sm font-light leading-relaxed tracking-wide mb-8 opacity-90">
                              {cards[0].currentMeaning.meaning}
                            </p>
                          </>
                        )}

                        <button
                          onClick={handleConfirmSpread}
                          className="group relative inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-amber-600 to-amber-500 text-black font-medium tracking-[0.2em] rounded-full hover:from-amber-500 hover:to-amber-400 transition-all duration-300 shadow-[0_0_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_40px_rgba(245,158,11,0.5)] active:scale-95"
                        >
                          <SafeIcon icon={FiIcons.FiMessageCircle} className="mr-3 text-lg" />
                          <span>帶著牌面回去對話</span>
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TarotTable;